import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { cn } from "@/lib/utils";

import { PrivacyPolicy } from "@/pages/PrivacyPolicy";
import { TermsAndConditions } from "@/pages/TermsAndConditions";
import {
  generateTestToken,
  isDevelopmentEnvironment,
  signInWithCustomToken,
  signInWithGithub,
  signInWithGoogle,
  signInWithMicrosoft,
} from "@/utils/auth";
import { Github, Mail, TestTube } from "lucide-react";
import React, { useState } from "react";

export function LoginForm({
  className,
  ...props
}: React.ComponentPropsWithoutRef<"div">) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const handleGoogleLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signInWithGoogle();
    } catch (error) {
      setError("Google login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleGithubLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signInWithGithub();
    } catch (error: any) {
      // Handle specific error messages
      if (error.message && error.message.includes("already exists")) {
        setError(error.message);
      } else {
        setError("GitHub login failed. Please try again.");
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleMicrosoftLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      await signInWithMicrosoft();
    } catch (error) {
      setError("Microsoft login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCustomLogin = async () => {
    setIsLoading(true);
    setError(null);
    try {
      // Generate a test token
      const token = await generateTestToken();
      
      // Sign in with the custom token
      await signInWithCustomToken(token);
      
      // Redirect to main app
      window.location.href = "/";
    } catch (error: any) {
      setError(error.message || "Custom login failed. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div
      className={cn("flex flex-col gap-6 w-full m-16 max-w-lg", className)}
      {...props}
    >
      <Card>
        <CardHeader className="text-center">
          <CardTitle className="text-xl flex items-center gap-2">
            <img src="/quickdb-logo.png" alt="" className="h-14 w-12" />

            <h1 className="text-3xl font-bold">
              <span> Welcome back to </span>
              <span className="text-black">Quick</span>
              <span className="text-yellow-600">DB</span>
            </h1>
          </CardTitle>
          <CardDescription>Login with your preferred account</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-6">
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
                {error}
              </div>
            )}

            <div className="flex flex-col gap-4">
              <Button
                variant="outline"
                className="w-full"
                onClick={handleGoogleLogin}
                disabled={isLoading}
              >
                <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                  <path
                    d="M12.48 10.92v3.28h7.84c-.24 1.84-.853 3.187-1.787 4.133-1.147 1.147-2.933 2.4-6.053 2.4-4.827 0-8.6-3.893-8.6-8.72s3.773-8.72 8.6-8.72c2.6 0 4.507 1.027 5.907 2.347l2.307-2.307C18.747 1.44 16.133 0 12.48 0 5.867 0 .307 5.387.307 12s5.56 12 12.173 12c3.573 0 6.267-1.173 8.373-3.36 2.16-2.16 2.84-5.213 2.84-7.667 0-.76-.053-1.467-.173-2.053H12.48z"
                    fill="currentColor"
                  />
                </svg>
                {isLoading ? "Signing in..." : "Continue with Google"}
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={handleGithubLogin}
                disabled={isLoading}
              >
                <Github className="w-5 h-5 mr-2" />
                {isLoading ? "Signing in..." : "Continue with GitHub"}
              </Button>

              <Button
                variant="outline"
                className="w-full"
                onClick={handleMicrosoftLogin}
                disabled={isLoading}
              >
                <Mail className="w-5 h-5 mr-2" />
                {isLoading ? "Signing in..." : "Continue with Microsoft"}
              </Button>

              {/* Custom Login Button - Only show in development */}
              {isDevelopmentEnvironment() && (
                <Button
                  variant="outline"
                  className="w-full border-orange-200 text-orange-700 hover:bg-orange-50"
                  onClick={handleCustomLogin}
                  disabled={isLoading}
                >
                  <TestTube className="w-5 h-5 mr-2" />
                  {isLoading ? "Signing in..." : "Test Login (Dev Only)"}
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
      <div className="text-balance text-center text-xs text-muted-foreground [&_a]:underline [&_a]:underline-offset-4 [&_a]:hover:text-primary  ">
        By clicking continue, you agree to our{" "}
        <button
          type="button"
          onClick={() => setShowTerms(true)}
          className="underline underline-offset-4 hover:text-primary"
        >
          Terms of Service
        </button>{" "}
        and{" "}
        <button
          type="button"
          onClick={() => setShowPrivacy(true)}
          className="underline underline-offset-4 hover:text-primary"
        >
          Privacy Policy
        </button>
        .
      </div>

      {/* Version display */}
      <div className="text-center text-xs text-muted-foreground">
        Version {__APP_VERSION__}
      </div>

      <TermsAndConditions
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
      />
      <PrivacyPolicy
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
      />
    </div>
  );
}
